var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { MulticastDelegate, MulticastDelegate1 } from "flavr-common";
export class Producer {
    constructor({ id, localId, rtpSender, transceiver, stream, track, stopTracks, disableTrackOnPause, zeroRtpOnPause }) {
        this._closed = false;
        this.onCloseDelegate = new MulticastDelegate();
        this.onTransportcloseDelegate = new MulticastDelegate();
        this.onPauseDelegate = new MulticastDelegate();
        this.onResumeDelegate = new MulticastDelegate();
        this.onTrackendedDelegate = new MulticastDelegate();
        this.onReplacetrackDelegate = new MulticastDelegate1();
        this.onSetmaxspatiallayerDelegate = new MulticastDelegate1();
        this.onSetrtpencodingparametersDelegate = new MulticastDelegate1();
        this.onNegotiationNeededDelegate = new MulticastDelegate();
        this.onTrackEnded = () => {
            this.onTrackendedDelegate.invoke();
        };
        this.onTrackMuted = () => { };
        this.onTrackUnmuted = () => { };
        this.onRemoteTrackEnded = () => { };
        this.onRemoteTrackMuted = () => { };
        this.onRemoteTrackUnmuted = () => { };
        this._id = id;
        this._localId = localId;
        this._rtpSender = rtpSender;
        this._transceiver = transceiver;
        this._stream = stream;
        this._track = track;
        this._kind = track.kind;
        this._paused = disableTrackOnPause ? !track.enabled : false;
        this._stopTracks = stopTracks;
        this._disableTrackOnPause = disableTrackOnPause;
        this._zeroRtpOnPause = zeroRtpOnPause;
        this.handleTrack();
    }
    get id() {
        return this._id;
    }
    get localId() {
        return this._localId;
    }
    get mid() {
        return this.transceiver.mid;
    }
    get closed() {
        return this._closed;
    }
    get paused() {
        return this._paused;
    }
    get kind() {
        return this._kind;
    }
    get transceiver() {
        return this._transceiver;
    }
    get rtpSender() {
        return this._rtpSender;
    }
    get stream() {
        return this._stream;
    }
    get track() {
        return this._track;
    }
    get muted() {
        if (this._closed)
            return true;
        if (!this._stream.active)
            return true;
        if (this._transceiver.stopped)
            return true;
        if (this._transceiver.currentDirection === "sendonly")
            return false;
        return this._transceiver.currentDirection !== "sendrecv";
    }
    get direction() {
        if (this._transceiver.currentDirection === null) {
            return "null";
        }
        return this._transceiver.currentDirection;
    }
    close() {
        if (this._closed)
            return;
        this._closed = true;
        this.destroyTrack();
        this.onCloseDelegate.invoke();
        this.clear();
    }
    transportClosed() {
        if (this._closed)
            return;
        this._closed = true;
        this.destroyTrack();
        this.onTransportcloseDelegate.invoke();
        this.onCloseDelegate.invoke();
        this.clear();
    }
    clear() {
        this.onCloseDelegate.clear();
        this.onTransportcloseDelegate.clear();
        this.onPauseDelegate.clear();
        this.onResumeDelegate.clear();
        this.onTrackendedDelegate.clear();
        this.onReplacetrackDelegate.clear();
        this.onSetmaxspatiallayerDelegate.clear();
        this.onSetrtpencodingparametersDelegate.clear();
        this.onNegotiationNeededDelegate.clear();
    }
    pause() {
        if (this._closed) {
            console.error("[multiplayer] pause() | Producer closed");
            return;
        }
        this._paused = true;
        if (this._track && this._disableTrackOnPause) {
            this._track.enabled = false;
        }
        if (this._zeroRtpOnPause) {
            this.onReplacetrackDelegate.invoke(null);
        }
        this.onPauseDelegate.invoke();
    }
    resume() {
        if (this._closed) {
            console.error("[multiplayer] resume() | Producer closed");
            return;
        }
        this._paused = false;
        if (this._track && this._disableTrackOnPause) {
            this._track.enabled = true;
        }
        if (this._zeroRtpOnPause) {
            this.onReplacetrackDelegate.invoke(this._track);
        }
        this.onResumeDelegate.invoke();
    }
    enable(enabled) {
        if (!this._transceiver)
            return;
        const newDirection = enabled ? "sendonly" : "inactive";
        if (this._transceiver.direction === newDirection)
            return;
        this._transceiver.direction = newDirection;
        this.onNegotiationNeededDelegate.invoke();
    }
    replaceTrack(track) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this._closed) {
                if (track && this._stopTracks) {
                    try {
                        track.stop();
                    }
                    catch (error) { }
                }
                throw new Error("closed");
            }
            else if (track && track.readyState === "ended") {
                throw new Error("track ended");
            }
            if (track === this._track) {
                return;
            }
            if (!this._zeroRtpOnPause || !this._paused) {
                yield this.onReplacetrackDelegate.invokeAsPromise(track);
            }
            this.destroyTrack();
            this._track = track;
            if (this._track && this._disableTrackOnPause) {
                if (!this._paused)
                    this._track.enabled = true;
                else if (this._paused)
                    this._track.enabled = false;
            }
            // Handle the effective track.
            this.handleTrack();
        });
    }
    /**
     * Needs reconnect?
     * @param codecs
     * @returns
     */
    setCodecPreferences(codecs) {
        if (!("setCodecPreferences" in window.RTCRtpTransceiver.prototype))
            return;
        this._transceiver.setCodecPreferences(codecs);
    }
    handleTrack() {
        if (!this._track)
            return;
        this._track.addEventListener("ended", this.onTrackEnded);
        this._track.addEventListener("mute", this.onTrackMuted);
        this._track.addEventListener("unmute", this.onTrackUnmuted);
        if (this.transceiver.receiver && this.transceiver.receiver.track) {
            this.transceiver.receiver.track.addEventListener("ended", this.onRemoteTrackEnded);
            this.transceiver.receiver.track.addEventListener("mute", this.onRemoteTrackMuted);
            this.transceiver.receiver.track.addEventListener("unmute", this.onRemoteTrackUnmuted);
        }
    }
    destroyTrack() {
        if (this.transceiver.receiver && this.transceiver.receiver.track) {
            this.transceiver.receiver.track.removeEventListener("ended", this.onRemoteTrackEnded);
            this.transceiver.receiver.track.removeEventListener("mute", this.onRemoteTrackMuted);
            this.transceiver.receiver.track.removeEventListener("unmute", this.onRemoteTrackUnmuted);
        }
        if (!this._track)
            return;
        try {
            this._track.removeEventListener("ended", this.onTrackEnded);
            this._track.removeEventListener("mute", this.onTrackMuted);
            this._track.removeEventListener("unmute", this.onTrackUnmuted);
            if (this._stopTracks)
                this._track.stop();
        }
        catch (_a) { }
    }
}
